import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { 
  WebSocketClient, 
  WebSocketClientOptions, 
  ConnectionStatus, 
  ClientMessage, 
  ServerMessage, 
  TranscriptionMessage 
} from '../lib/websocketClient';

export interface UseWebSocketOptions extends Omit<WebSocketClientOptions, 'url'> {
  autoConnect?: boolean;
}

export interface UseWebSocketReturn {
  client: WebSocketClient | null;
  status: ConnectionStatus;
  connect: () => Promise<void>;
  disconnect: () => void;
  sendMessage: (message: ClientMessage) => boolean;
  isConnected: boolean;
  error: Error | null;
  lastMessage: ServerMessage | null;
  lastTranscription: TranscriptionMessage | null;
}

export function useWebSocket(
  url: string | null, 
  options: UseWebSocketOptions = {}
): UseWebSocketReturn {
  const { autoConnect = true, ...clientOptions } = options;
  
  // Memoize clientOptions to prevent unnecessary re-creations
  const stableClientOptions = useMemo(() => clientOptions, [
    clientOptions.maxReconnectAttempts,
    clientOptions.initialReconnectDelay,
    clientOptions.maxReconnectDelay,
    clientOptions.reconnectBackoffFactor
  ]);
  
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [error, setError] = useState<Error | null>(null);
  const [lastMessage, setLastMessage] = useState<ServerMessage | null>(null);
  const [lastTranscription, setLastTranscription] = useState<TranscriptionMessage | null>(null);
  
  const clientRef = useRef<WebSocketClient | null>(null);
  const isConnected = status === 'connected';

  // Initialize client when URL changes
  useEffect(() => {
    console.log('🔄 useWebSocket useEffect triggered:', { url, autoConnect });
    
    if (!url) {
      console.log('❌ No URL, clearing client');
      if (clientRef.current) {
        console.log('🔌❌ Clearing clientRef.current due to no URL');
        clientRef.current.disconnect();
        clientRef.current = null;
      }
      return;
    }

    // Create new client
    const client = new WebSocketClient({
      url,
      ...stableClientOptions
    });

    // Set up event handlers
    client.on('onStatusChange', (newStatus) => {
      console.log('📥 useWebSocket received status change:', newStatus);
      setStatus(newStatus);
      if (newStatus !== 'error') {
        setError(null);
      }
      console.log('🔄 useWebSocket state updated to:', newStatus);
    });

    client.on('onError', (err) => {
      setError(err);
    });

    client.on('onMessage', (message) => {
      setLastMessage(message);
    });

    client.on('onTranscription', (transcription) => {
      setLastTranscription(transcription);
    });

    // Assign client to ref immediately
    clientRef.current = client;
    console.log('🔧 WebSocket client created and assigned to ref:', {
      clientId: client.toString(),
      url: client.getStatus ? client.getStatus() : 'no status method'
    });

    // Auto-connect if enabled - delay to ensure proper state sync
    if (autoConnect) {
      console.log('🚀 Starting auto-connect...');
      setTimeout(() => {
        client.connect().then(() => {
          console.log('✅ Auto-connect successful, client status:', client.getStatus());
          console.log('✅ Client readyState:', client.getReadyState());
        }).catch((err) => {
          console.error('❌ Auto-connect failed:', err);
        });
      }, 0);
    }

    // Cleanup on unmount or URL change
    return () => {
      console.log('🧹 useWebSocket cleanup - disconnecting client');
      client.disconnect();
    };
  }, [url, autoConnect, stableClientOptions]);

  const connect = useCallback(async () => {
    if (!clientRef.current) {
      console.error('❌ Cannot connect: WebSocket client not initialized');
      throw new Error('WebSocket client not initialized');
    }
    console.log('🔌 Manually connecting WebSocket...');
    await clientRef.current.connect();
    console.log('✅ Manual connect completed');
  }, []);

  const disconnect = useCallback(() => {
    console.log('🔌❌ useWebSocket.disconnect() called');
    if (clientRef.current) {
      clientRef.current.disconnect();
    }
  }, []);

  const sendMessage = useCallback((message: ClientMessage): boolean => {
    console.log('📤 useWebSocket.sendMessage called:', {
      messageType: message.type,
      hasClient: !!clientRef.current,
      clientConnected: clientRef.current?.isConnected(),
      clientStatus: clientRef.current?.getStatus()
    });
    
    if (!clientRef.current) {
      console.error('❌ sendMessage: No client available');
      return false;
    }
    
    const result = clientRef.current.sendMessage(message);
    console.log('📤 sendMessage result:', result);
    return result;
  }, []);

  // Add debug logging for client reference
  useEffect(() => {
    console.log('🔗 clientRef.current changed:', {
      hasClient: !!clientRef.current,
      clientStatus: clientRef.current?.getStatus(),
      hookStatus: status
    });
  }, [clientRef.current, status]);

  return {
    client: clientRef.current,
    status,
    connect,
    disconnect,
    sendMessage,
    isConnected,
    error,
    lastMessage,
    lastTranscription
  };
}

// Specialized hooks for different use cases
export function useStreamerWebSocket(roomId: string | null, options: UseWebSocketOptions = {}) {
  const baseWsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8787';
  const wsUrl = roomId ? `${baseWsUrl}/ws/${roomId}` : null;
  const webSocket = useWebSocket(wsUrl, options);

  const startStream = useCallback(() => {
    console.log('🚀 startStream called:', {
      roomId,
      hasClient: !!webSocket.client,
      clientConnected: webSocket.client?.isConnected(),
      hookStatus: webSocket.status
    });
    
    if (!roomId) return false;
    
    const result = webSocket.sendMessage({
      type: 'start_stream',
      roomId
    });
    
    console.log('📤 startStream result:', result);
    return result;
  }, [roomId, webSocket.sendMessage, webSocket.client, webSocket.status]);

  const sendAudioChunk = useCallback((audioData: ArrayBuffer, timestamp: number, sequenceId: number) => {
    console.log('useStreamerWebSocket.sendAudioChunk called:', {
      size: audioData.byteLength,
      timestamp,
      sequenceId
    });
    
    return webSocket.sendMessage({
      type: 'audio_chunk',
      data: audioData,
      timestamp,
      sequenceId
    });
  }, [webSocket.sendMessage]);

  return {
    // Don't spread webSocket - it creates stale closure
    client: webSocket.client,
    status: webSocket.status,
    connect: webSocket.connect,
    disconnect: webSocket.disconnect,
    sendMessage: webSocket.sendMessage,
    isConnected: webSocket.isConnected,
    error: webSocket.error,
    lastMessage: webSocket.lastMessage,
    lastTranscription: webSocket.lastTranscription,
    startStream,
    sendAudioChunk
  };
}

export function useViewerWebSocket(roomId: string | null, options: UseWebSocketOptions = {}) {
  const baseWsUrl = import.meta.env.VITE_WS_URL || 'ws://localhost:8787';
  const wsUrl = roomId ? `${baseWsUrl}/ws/${roomId}` : null;
  const webSocket = useWebSocket(wsUrl, options);

  const joinRoom = useCallback(() => {
    if (!roomId) return false;
    
    return webSocket.sendMessage({
      type: 'join_room',
      roomId
    });
  }, [roomId, webSocket]);

  // Auto-join room when connected
  useEffect(() => {
    if (webSocket.isConnected && roomId) {
      joinRoom();
    }
  }, [webSocket.isConnected, roomId, joinRoom]);

  return {
    // Don't spread webSocket - it creates stale closure
    client: webSocket.client,
    status: webSocket.status,
    connect: webSocket.connect,
    disconnect: webSocket.disconnect,
    sendMessage: webSocket.sendMessage,
    isConnected: webSocket.isConnected,
    error: webSocket.error,
    lastMessage: webSocket.lastMessage,
    lastTranscription: webSocket.lastTranscription,
    joinRoom
  };
}